import type { AudienceMode, AudienceOption, NotificationPayloadMap } from "../types";

import api from "@/services/axios";

type ApiError = {
  code?: string | null;
  message?: string | null;
  type?: string | null;
  target?: string | null;
  details?: Record<string, string[]> | null;
};

type ApiResponse<T> = {
  data?: T;
  errors?: ApiError[] | null;
  success?: boolean;
  message?: string;
};

type PagedResult<T> = {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
};

type UserListItemDto = {
  id: string;
  name?: string | null;
  role?: string | null;
};

type RoleSummaryDto = {
  id: string;
  name?: string | null;
  description?: string | null;
};

export type NotificationInboxItem = {
  recipientId: string;
  notificationId: string;
  title: string;
  body: string;
  isRead: boolean;
  sentAtUtc: string | null;
  readAtUtc: string | null;
};

function unwrapApiResponse<T>(payload: ApiResponse<T>): T {
  if (payload?.success === false) {
    throw new Error(payload.message || "Request failed");
  }

  const firstError = payload?.errors?.[0]?.message;
  if (firstError) {
    throw new Error(firstError);
  }

  if (payload?.data === undefined) {
    throw new Error(payload?.message || "Unexpected API response");
  }

  return payload.data;
}

export async function fetchAudienceOptions(
  mode: Extract<AudienceMode, "roles" | "users">,
  search: string,
): Promise<AudienceOption[]> {
  const normalizedSearch = search.trim().toLowerCase();

  if (mode === "roles") {
    const response = await api.get<ApiResponse<RoleSummaryDto[]>>("/user-access/roles");
    const roles = unwrapApiResponse(response.data);

    return (roles ?? [])
      .filter((role) => {
        const name = String(role.name ?? "").toLowerCase();
        return normalizedSearch.length === 0 || name.includes(normalizedSearch);
      })
      .map((role) => ({
        id: String(role.id),
        label: String(role.name ?? "Unnamed role"),
        subtitle: role.description ?? undefined,
      }));
  }

  const response = await api.get<ApiResponse<PagedResult<UserListItemDto>>>(
    "/user-access/users",
    {
      params: {
        q: search.trim() || undefined,
        pageNumber: 1,
        pageSize: 100,
        status: "active",
      },
    },
  );

  const data = unwrapApiResponse(response.data);

  return (data.items ?? []).map((user) => ({
    id: String(user.id),
    label: String(user.name ?? "Unnamed user"),
    subtitle: user.role ?? undefined,
  }));
}

export async function resolveRoleUserIds(roleIds: string[]): Promise<string[]> {
  const results = await Promise.all(
    roleIds.map((roleId) =>
      api
        .get<ApiResponse<PagedResult<UserListItemDto>>>("/user-access/users", {
          params: { roleId, pageNumber: 1, pageSize: 100, status: "active" },
        })
        .then((r) => unwrapApiResponse(r.data).items ?? []),
    ),
  );

  return Array.from(new Set(results.flat().map((user) => String(user.id))));
}

export async function sendToUsers(
  userIds: string[],
  title: string,
  body: string,
  data?: NotificationPayloadMap,
): Promise<string> {
  const response = await api.post<ApiResponse<string>>("/notifications/send", {
    userIds,
    title,
    body,
    data,
  });
  return unwrapApiResponse(response.data);
}

export async function sendTopic(
  topicName: string,
  title: string,
  body: string,
  data?: NotificationPayloadMap,
): Promise<string> {
  const response = await api.post<ApiResponse<string>>("/notifications/send-topic", {
    topic: topicName,
    title,
    body,
    data,
  });
  return unwrapApiResponse(response.data);
}

export async function fetchUnreadCount(): Promise<number> {
  const response = await api.get<ApiResponse<number>>("/notifications/unread-count");
  return unwrapApiResponse(response.data);
}

export async function markAllRead(): Promise<number> {
  const response = await api.post<ApiResponse<number>>("/notifications/mark-all-read");
  return unwrapApiResponse(response.data);
}

export async function fetchNotifications(
  pageNumber: number,
  pageSize: number,
  isRead?: boolean,
): Promise<PagedResult<NotificationInboxItem>> {
  const params: Record<string, string> = {
    pageNumber: String(pageNumber),
    pageSize: String(pageSize),
  };
  if (isRead !== undefined) {
    params.isRead = String(isRead);
  }
  const response = await api.get<ApiResponse<PagedResult<NotificationInboxItem>>>(
    "/notifications",
    { params },
  );
  return unwrapApiResponse(response.data);
}

export async function markNotificationRead(recipientId: string): Promise<boolean> {
  const response = await api.post<ApiResponse<boolean>>(
    `/notifications/${recipientId}/mark-read`,
  );
  return unwrapApiResponse(response.data);
}
